<template>
    <div>
        <div v-if="showValidationErrors && unmappedRequiredFields.length > 0" class="alert alert-danger mb-3">
            <i class="icofont-warning-alt mr-1"></i>
            {{ trans('Please map the following required fields:') }}
            <strong>{{ unmappedRequiredFields.map(f => f.label).join(', ') }}</strong>
        </div>

        <div class="table-responsive">
            <table class="table">
                <thead>
                    <tr>
                        <th style="width: 25%;">{{ trans('Prixo field') }}</th>
                        <th style="width: 35%;">{{ trans('File column') }}</th>
                        <th style="width: 40%;">{{ trans('Example value') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="field in visibleFields" :key="field.key" :class="{ 'table-danger': isFieldInvalid(field) }">
                        <td>
                            <label :for="'mapping-' + field.key" class="mb-0">
                                {{ field.label }}
                                <span v-if="isFieldRequired(field)" class="text-danger">*</span>
                            </label>
                            <small v-if="field.hint" class="text-muted d-block">{{ field.hint }}</small>
                        </td>
                        <td>
                            <select
                                :id="'mapping-' + field.key"
                                class="form-control form-control-sm"
                                :class="{ 'is-invalid': isFieldInvalid(field) }"
                                :value="localMapping[field.key]"
                                @change="updateMapping(field.key, $event.target.value)"
                            >
                                <option :value="null">-- {{ trans('Select column') }} --</option>
                                <option
                                    v-for="(header, index) in fileHeaders"
                                    :key="index"
                                    :value="index"
                                >
                                    {{ header }} ({{ trans('Column') }} {{ getColumnLetter(index) }})
                                </option>
                            </select>
                        </td>
                        <td>
                            <span class="text-muted" v-if="getExampleValue(field.key) !== null">
                                {{ getExampleValue(field.key) }}
                            </span>
                            <span class="text-muted font-italic" v-else>
                                {{ trans('No column selected') }}
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <hr>

        <h6>{{ trans('Options') }}</h6>

        <div class="form-check mb-2">
            <input
                type="checkbox"
                class="form-check-input"
                id="skip-first-row"
                :checked="localOptions.skip_first_row"
                @change="updateOption('skip_first_row', $event.target.checked)"
            />
            <label class="form-check-label" for="skip-first-row">
                {{ trans('Skip first row (header row)') }}
            </label>
        </div>

        <div class="form-check" v-if="uniqueKeyField">
            <input
                type="checkbox"
                class="form-check-input"
                id="use-internal-id"
                :checked="localOptions.use_internal_id_as_key"
                @change="updateOption('use_internal_id_as_key', $event.target.checked)"
            />
            <label class="form-check-label" for="use-internal-id">
                {{ trans('Use Internal ID to update existing records') }}
                <small class="text-muted d-block">
                    {{ trans('If checked, existing records with the same Internal ID will be updated instead of creating new ones.') }}
                </small>
            </label>
        </div>

        <div v-for="option in additionalOptions" :key="option.key" class="form-group mt-3">
            <label class="d-block mb-2">{{ option.label }}</label>
            <div v-if="option.type === 'pills'" class="btn-group btn-group-toggle">
                <button
                    v-for="(optLabel, optValue) in option.options"
                    :key="optValue"
                    type="button"
                    class="btn btn-sm"
                    :class="(localOptions[option.key] || option.default) === optValue ? 'btn-primary' : 'btn-outline-secondary'"
                    @click="updateOption(option.key, optValue)"
                >
                    {{ optLabel }}
                </button>
            </div>
            <select
                v-else-if="option.type === 'select'"
                :id="'option-' + option.key"
                class="form-control form-control-sm"
                style="max-width: 300px;"
                :value="localOptions[option.key] || option.default"
                @change="updateOption(option.key, $event.target.value)"
            >
                <option
                    v-for="(optLabel, optValue) in option.options"
                    :key="optValue"
                    :value="optValue"
                >
                    {{ optLabel }}
                </option>
            </select>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        prixoFields: {
            type: Array,
            required: true,
        },
        fileHeaders: {
            type: Array,
            required: true,
        },
        firstRow: {
            type: Array,
            default: () => [],
        },
        columnMapping: {
            type: Object,
            required: true,
        },
        options: {
            type: Object,
            required: true,
        },
        showValidationErrors: {
            type: Boolean,
            default: false,
        },
        uniqueKeyField: {
            type: String,
            default: null,
        },
        additionalOptions: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            localMapping: { ...this.columnMapping },
            localOptions: { ...this.options },
        };
    },

    watch: {
        columnMapping(newVal) {
            this.localMapping = { ...newVal };
        },
        options(newVal) {
            this.localOptions = { ...newVal };
        },
    },

    computed: {
        visibleFields() {
            return this.prixoFields.filter(field => {
                // Hide *_link_value fields when their corresponding *_link_method is 'none'
                if (field.key.endsWith('_link_value')) {
                    const methodKey = field.key.replace('_link_value', '_link_method');
                    const linkMethod = this.localOptions[methodKey] || 'none';
                    return linkMethod !== 'none';
                }
                return true;
            });
        },

        unmappedRequiredFields() {
            return this.visibleFields.filter(field => {
                if (!this.isFieldRequired(field)) return false;
                const mapping = this.localMapping[field.key];
                return mapping === null || mapping === undefined || mapping === '' || mapping === -1;
            });
        },
    },

    methods: {
        isFieldRequired(field) {
            // *_link_value is required when its link method is selected
            if (field.key.endsWith('_link_value')) {
                const methodKey = field.key.replace('_link_value', '_link_method');
                const linkMethod = this.localOptions[methodKey] || 'none';
                return linkMethod !== 'none';
            }
            return field.required;
        },

        isFieldInvalid(field) {
            if (!this.showValidationErrors || !this.isFieldRequired(field)) return false;
            const mapping = this.localMapping[field.key];
            return mapping === null || mapping === undefined || mapping === '' || mapping === -1;
        },

        updateMapping(fieldKey, value) {
            const intValue = value === '' || value === 'null' ? null : parseInt(value, 10);
            this.localMapping[fieldKey] = intValue;
            this.$emit('update:column-mapping', { ...this.localMapping });
        },

        updateOption(optionKey, value) {
            this.localOptions[optionKey] = value;
            this.$emit('update:options', { ...this.localOptions });
        },

        getExampleValue(fieldKey) {
            const columnIndex = this.localMapping[fieldKey];
            if (columnIndex === null || columnIndex === undefined || columnIndex < 0) {
                return null;
            }
            return this.firstRow[columnIndex] ?? null;
        },

        getColumnLetter(index) {
            let letter = '';
            let num = index;
            while (num >= 0) {
                letter = String.fromCharCode((num % 26) + 65) + letter;
                num = Math.floor(num / 26) - 1;
            }
            return letter;
        },
    },
};
</script>
